import { GET, POST, DOWNLOAD } from '../config';
import { IApiResponse } from '../../../interfaces/api-response.interface';
import {
  IHrApprovalsAddPayload,
  IHrApprovalResponse,
  IHrFetchInformationResponse,
  IHrFetchPayload,
  IHrFetchResponse,
  IHrApplicationExport,
  IHrApprovalsAddResponse,
  IDashboardFetchStaticDataResponse,
  IDashboardFetchApplicationPayload,
  IDashboardFetchApplicationResponse,
  IDashboardFetchApprovalPayload,
  IDashboardFetchApprovalResponse,
  IDashboardApplicationExport,
  IDashboardApplicationExportPayload,
} from '../../../interfaces/hr.interface';
import { IFilterForm } from '../../../interfaces/filter-form.interface';
import { IEditHrInformationModal } from '../../../interfaces/modal.interface';

export interface IHrApi {
  addApprover: (
    payload?: string | null,
    params?: IHrApprovalsAddPayload,
  ) => Promise<IHrApprovalsAddResponse>;
  applicationExport: (payload?: any, params?: IFilterForm) => Promise<IHrApplicationExport>;
  delete: (payload: string, params?: string) => Promise<IApiResponse>;
  deleteApprover: (payload: string | null, params?: string) => Promise<IHrApprovalResponse>;
  downloadHrApplicationList: (slug?: string) => string;
  downloadInformationPDF: (slug?: string) => void;
  generateCOE: (slug?: string) => void;
  downloadExitInterviewCSV: (slug?: string) => void;
  downloadExitInterviewPDF: (slug?: string) => void;
  downloadClearanceFormPDF: (slug?: string) => void;
  editInformation: (
    payload: string,
    params: IEditHrInformationModal,
  ) => Promise<IHrFetchInformationResponse>;
  fetch: (payload: IHrFetchPayload) => Promise<IHrFetchResponse>;
  fetchInformation: (payload: string, params?: any) => Promise<IHrFetchInformationResponse>;
  //  For Dashboard API
  fetchStaticDashboardData: () => Promise<IDashboardFetchStaticDataResponse>;
  fetchApplicationDashboardData: (
    payload: IDashboardFetchApplicationPayload,
  ) => Promise<IDashboardFetchApplicationResponse>;
  applicationDashboardExport: (
    payload: IDashboardApplicationExportPayload,
  ) => Promise<IDashboardApplicationExport>;
  downloadApplicationDashboardData: (token: string) => string;
  fetchApprovalDashboardData: (
    payload: IDashboardFetchApprovalPayload,
  ) => Promise<IDashboardFetchApprovalResponse>;
}

export default {
  addApprover: (
    payload?: string | null,
    params?: IHrApprovalsAddPayload,
  ): Promise<IHrApprovalsAddResponse> => {
    return POST('/api/v1/approvals/create/', { ...params }).then((res) => res.data);
  },

  applicationExport: (payload?: any, params?: IFilterForm): Promise<IHrApplicationExport> => {
    return POST('/api/v1/application/export/', { ...params }).then((res) => res.data);
  },

  delete: (payload: string, params?: string): Promise<IApiResponse> => {
    return POST(`/api/v1/application/${payload}/cancel/`, { remarks: params }).then(
      (res) => res.data,
    );
  },

  deleteApprover: (payload?: string | null, params?: string): Promise<IHrApprovalResponse> => {
    return POST('/api/v1/approvals/delete/', { approval: params }).then((res) => res.data);
  },

  downloadHrApplicationList: (token?: string) => {
    const url = `/api/v1/application/export/${token}/`;

    return DOWNLOAD(url);
  },

  downloadInformationPDF: (slug?: string) => {
    const url = `/api/v1/application/${slug}/export/pdf/`;

    return DOWNLOAD(url);
  },

  generateCOE: (slug?: string) => {
    const url = `/api/v1/coe/${slug}/export/pdf/`;

    return DOWNLOAD(url);
  },

  downloadExitInterviewCSV: (slug?: string) => {
    const url = `/api/v1/application-exit-interview/${slug}/export/csv/`;

    return DOWNLOAD(url);
  },

  downloadExitInterviewPDF: (slug?: string) => {
    const url = `/api/v1/application-exit-interview/${slug}/export/pdf/`;

    return DOWNLOAD(url);
  },

  downloadClearanceFormPDF: (slug?: string) => {
    const url = `/api/v1/application-clearance-form/${slug}/export/pdf/`;

    return DOWNLOAD(url);
  },

  editInformation: (
    payload: string,
    params: IEditHrInformationModal,
  ): Promise<IHrFetchInformationResponse> => {
    return POST(`/api/v1/application/${payload}/edit/`, { ...params }).then((res) => res.data);
  },

  fetch: (payload: IHrFetchPayload, params?: any): Promise<IHrFetchResponse> => {
    return GET('/api/v1/application/list/', { params: payload }).then((res) => res.data);
  },

  fetchInformation: (payload: string, params?: any): Promise<IHrFetchInformationResponse> => {
    return GET(`/api/v1/application/${payload}/`, { params }).then((res) => res.data);
  },

  //  For Dashboard API
  fetchStaticDashboardData: (): Promise<IDashboardFetchStaticDataResponse> => {
    return GET('/api/v1/static/report/').then((res) => res.data);
  },

  fetchApplicationDashboardData: (
    payload: IDashboardFetchApplicationPayload,
  ): Promise<IDashboardFetchApplicationResponse> => {
    return POST('/api/v1/application/report/', payload).then((res) => res.data);
  },

  applicationDashboardExport: (payload: IDashboardApplicationExportPayload) => {
    return POST('/api/v1/application/report/', payload).then((res) => res.data);
  },

  downloadApplicationDashboardData: (token: string) => {
    const url = `/api/v1/application/report/export/${token}/`;

    return DOWNLOAD(url);
  },

  fetchApprovalDashboardData: (
    payload: IDashboardFetchApprovalPayload,
  ): Promise<IDashboardFetchApprovalResponse> => {
    return POST('/api/v1/approval/report/', payload).then((res) => res.data);
  },
};
